import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid';

import { classNames, noop } from '../../utils';
import { Button } from "../button";
import { PlusIcon } from "@heroicons/react/outline";
import { useDrawer } from "../../shared/drawer";

export const SelectVehicle = ({
    name,
    label,
    disabled,
    value = null,
    items = [],
    placeholder,
    onChange = noop,
    noMargin = false,
    hasAdd = false,
    addName = "",
    AddForm
}: {
    name: string;
    label?: string;
    noMargin?: boolean;
    disabled?: boolean;
    hasAdd?: boolean;
    addName?: string | null;
    AddForm?: any;
    placeholder?: string;
    items: { id: number | string; plate: string }[];
    value?: { id: number | string; plate: string } | any;
    onChange: (item: any) => void;
}) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<any>(null);

    useEffect(() => {
        if (value && items) {
            const selectedItem = items.find((i) => i.id === value);
            setSelected(selectedItem || null);
        } else {
            setSelected(null);
        }
    }, [value, items]);

    const handleChange = (item: any) => {
        setSelected(item);
        onChange(item);
    }

    return (
        <div className={classNames('w-full', noMargin ? 'mb-0' : 'mb-[24px]')}>
            <div className="flex flex-row w-full items-center">
                <div className="flex-1">
                    {label && (
                        <label className="pt-2 pb-1 flex items-end w-full block text-sm font-semibold text-gray-700">
                            {label}
                        </label>
                    )}
                    <Listbox value={selected} onChange={handleChange} name={name}>
                        {({ open }) => (
                            <>
                                <div className="relative">
                                    <Listbox.Button
                                        className={classNames(
                                            'flex items-center',
                                            open ? 'border-b-blue-500' : '',
                                            disabled
                                                ? 'text-gray-darker bg-gray-light not-italic font-light leading-6 h-[40px] relative w-full border-b-2 border-b-gray-dark shadow-sm pl-3 pr-10 text-left cursor-default'
                                                : 'bg-transparent not-italic leading-6 h-[40px] relative w-full border-b-2 border-b-black shadow-sm pl-3 pr-10 text-left cursor-default text-black'
                                        )}
                                        data-testid={`select-${name}`}
                                    >
                                        <span className="block truncate">{selected?.plate || placeholder || ''}</span>
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                            <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </span>
                                    </Listbox.Button>

                                    <Transition
                                        show={!disabled && open}
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                            {[{ id: null, plate: '-' }, ...items].map((element) => (
                                                <Listbox.Option
                                                    key={element.id}
                                                    data-testid={`select-${name}`}
                                                    className={({ active }) =>
                                                        classNames(
                                                            active ? 'text-white bg-blue-600' : 'text-gray-900',
                                                            'cursor-default select-none relative py-2 pl-3 pr-9'
                                                        )
                                                    }
                                                    value={element}
                                                >
                                                    {({ selected, active }) => (
                                                        <>
                                                            <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                                                {element.plate}
                                                            </span>
                                                            {selected && (
                                                                <span className={classNames(
                                                                    active ? 'text-white' : 'text-blue-600',
                                                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                                                )}>
                                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Listbox.Option>
                                            ))}
                                        </Listbox.Options>
                                    </Transition>
                                </div>
                            </>
                        )}
                    </Listbox>
                </div>
            </div>
        </div>
    );
};
